/**
 * FeedbackRow — 5-emoji rating card. Shows after 5th calculation, once per 30 days.
 */

import { useState, useMemo } from 'react';
import { doc, updateDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { useAuth } from '@/contexts/AuthContext';
import { T } from '@/styles/tokens';

interface FeedbackRowProps {
  verdictCount: number;
}

const EMOJIS = [
  { emoji: '😍', score: 5 },
  { emoji: '😊', score: 4 },
  { emoji: '😄', score: 3 },
  { emoji: '😐', score: 2 },
  { emoji: '😟', score: 1 },
];

const THIRTY_DAYS = 30 * 86400000;

export function FeedbackRow({ verdictCount }: FeedbackRowProps) {
  const { user, profile } = useAuth();
  const [phase, setPhase] = useState<'ask' | 'text' | 'thanks' | 'hidden'>('ask');
  const [feedbackText, setFeedbackText] = useState('');

  const shouldShow = useMemo(() => {
    if (verdictCount < 5) return false;
    const last = profile?.lastFeedbackAt;
    if (last && Date.now() - last < THIRTY_DAYS) return false;
    return true;
  }, [verdictCount, profile?.lastFeedbackAt]);

  if (!shouldShow || phase === 'hidden') return null;

  const handleScore = async (score: number) => {
    if (user) {
      addDoc(collection(db, 'users', user.uid, 'feedback'), {
        score,
        timestamp: serverTimestamp(),
        appVersion: '1.0.0',
      }).catch(console.error);
      updateDoc(doc(db, 'users', user.uid), {
        lastFeedbackAt: Date.now(),
      }).catch(console.error);
    }

    if (score >= 4) {
      setPhase('thanks');
      // TODO: Trigger App Store review via Capacitor plugin when available
      setTimeout(() => setPhase('hidden'), 2000);
    } else {
      setPhase('text');
    }
  };

  const handleSubmitText = async () => {
    if (user && feedbackText.trim()) {
      // Update the most recent feedback doc with text
      addDoc(collection(db, 'users', user.uid, 'feedback'), {
        score: 0,
        text: feedbackText.trim(),
        timestamp: serverTimestamp(),
        appVersion: '1.0.0',
      }).catch(console.error);
    }
    setPhase('hidden');
  };

  return (
    <div style={{
      background: T.white, borderRadius: T.radiusMd, padding: 20,
      boxShadow: T.shadowRaised, marginBottom: 12, textAlign: 'center',
      fontFamily: T.fontFamily,
    }}>
      {phase === 'ask' && (
        <>
          <div style={{
            fontSize: 11, fontWeight: 700, letterSpacing: '1.2px',
            color: T.textMuted, textTransform: 'uppercase' as const, marginBottom: 14,
          }}>
            HOW ARE YOU LIKING TRUEPRICE?
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
            {EMOJIS.map(e => (
              <button key={e.score} onClick={() => handleScore(e.score)} style={{
                fontSize: 28, background: 'none', border: 'none', cursor: 'pointer',
                padding: 4, borderRadius: 8, minWidth: 44, minHeight: 44,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {e.emoji}
              </button>
            ))}
          </div>
        </>
      )}

      {phase === 'thanks' && (
        <div style={{ fontSize: 14, fontWeight: 600, color: T.text, padding: '8px 0' }}>
          Noted.
        </div>
      )}

      {phase === 'text' && (
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 8 }}>
            What could be better?
          </div>
          <textarea
            value={feedbackText}
            onChange={e => setFeedbackText(e.target.value)}
            rows={3}
            style={{
              width: '100%', padding: '12px 14px', borderRadius: 12,
              border: `1.5px solid ${T.gray200 || '#E5E7EB'}`, fontSize: 13,
              fontFamily: T.fontFamily, resize: 'none', outline: 'none',
              boxSizing: 'border-box',
            }}
            placeholder="Tell us..."
          />
          <div style={{ textAlign: 'center' }}>
            <button onClick={handleSubmitText} style={{
              marginTop: 10, padding: '10px 20px', borderRadius: 26,
              background: T.primary, color: T.white, fontSize: 13, fontWeight: 600,
              border: 'none', cursor: 'pointer', fontFamily: T.fontFamily,
            }}>
              Submit
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
